import React from 'react';
import {
  FileText,
  MessageCircle,
  Users,
  Calendar,
  Activity,
  BookOpen,
  Brain,
  Heart,
  Puzzle,
  Target,
  ClipboardList,
  Shield,
  CheckCircle,
  Star,
  Lightbulb,
} from 'lucide-react';

// Section icon/color mapping for treatment plan headings (Portuguese keywords)
export const getSectionStyle = (text) => {
  const t = (text || '').toUpperCase();
  // Admin (amber)
  if (t.includes('RESUMO') || t.includes('EXECUTIVO') || t.includes('SUMMARY'))
    return { icon: FileText, color: 'amber', gradient: 'from-amber-500 to-amber-600', bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', iconColor: 'text-amber-600' };
  // Clinical (brand)
  if (t.includes('COMUNICA') || t.includes('LINGUAGEM') || t.includes('LANGUAGE') || t.includes('FALA'))
    return { icon: MessageCircle, color: 'brand', gradient: 'from-brand-600 to-brand-700', bg: 'bg-brand-50', border: 'border-brand-400', text: 'text-brand-800', iconColor: 'text-brand-600' };
  if (t.includes('SOCIAL') || t.includes('INTERA'))
    return { icon: Users, color: 'brand', gradient: 'from-brand-600 to-brand-700', bg: 'bg-brand-50', border: 'border-brand-400', text: 'text-brand-800', iconColor: 'text-brand-600' };
  // Progress (emerald)
  if (t.includes('LINHA DO TEMPO') || t.includes('TIMELINE') || t.includes('IMPLEMENTA') || t.includes('CRONOGRAMA'))
    return { icon: Calendar, color: 'emerald', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-800', iconColor: 'text-emerald-600' };
  if (t.includes('MONITORAMENTO') || t.includes('PROGRESSO') || t.includes('MONITORING') || t.includes('PROGRESS'))
    return { icon: Activity, color: 'emerald', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-800', iconColor: 'text-emerald-600' };
  // Academic (slate)
  if (t.includes('ACADEM') || t.includes('COGNITIV') || t.includes('APRENDIZ'))
    return { icon: BookOpen, color: 'slate', gradient: 'from-slate-500 to-slate-600', bg: 'bg-slate-50', border: 'border-slate-400', text: 'text-slate-800', iconColor: 'text-slate-600' };
  // Clinical (brand)
  if (t.includes('COMPORTAMENT') || t.includes('BEHAVIOR'))
    return { icon: Brain, color: 'brand', gradient: 'from-brand-600 to-brand-700', bg: 'bg-brand-50', border: 'border-brand-400', text: 'text-brand-800', iconColor: 'text-brand-600' };
  if (t.includes('INDEPEND') || t.includes('VIDA') || t.includes('DIÁRI') || t.includes('AUTOCUIDADO') || t.includes('DAILY'))
    return { icon: Heart, color: 'brand', gradient: 'from-brand-600 to-brand-700', bg: 'bg-brand-50', border: 'border-brand-400', text: 'text-brand-800', iconColor: 'text-brand-600' };
  if (t.includes('MOTOR') || t.includes('SENSORI'))
    return { icon: Puzzle, color: 'brand', gradient: 'from-brand-600 to-brand-700', bg: 'bg-brand-50', border: 'border-brand-400', text: 'text-brand-800', iconColor: 'text-brand-600' };
  // Progress (emerald)
  if (t.includes('META') || t.includes('OBJETIVO') || t.includes('GOAL'))
    return { icon: Target, color: 'emerald', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-800', iconColor: 'text-emerald-600' };
  // Admin (amber)
  if (t.includes('RECURSO') || t.includes('MATERIAL') || t.includes('RESOURCE'))
    return { icon: ClipboardList, color: 'amber', gradient: 'from-amber-500 to-amber-600', bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', iconColor: 'text-amber-600' };
  if (t.includes('TROUBLESHOOT') || t.includes('DESAFIO') || t.includes('PROBLEMA'))
    return { icon: Shield, color: 'amber', gradient: 'from-amber-500 to-amber-600', bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', iconColor: 'text-amber-600' };
  // Progress (emerald)
  if (t.includes('QUALIDADE') || t.includes('QUALITY') || t.includes('CHECKLIST'))
    return { icon: CheckCircle, color: 'emerald', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-800', iconColor: 'text-emerald-600' };
  // Default
  return { icon: Star, color: 'gray', gradient: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', border: 'border-gray-400', text: 'text-gray-800', iconColor: 'text-gray-600' };
};

// Extract plain text from ReactMarkdown children
export const extractText = (children) => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children?.props?.children) return extractText(children.props.children);
  return '';
};

// Custom markdown renderers for treatment plan
export const treatmentPlanComponents = {
  h1: ({ children }) => {
    const text = extractText(children);
    const style = getSectionStyle(text);
    const Icon = style.icon;
    return (
      <div className={`bg-gradient-to-r ${style.gradient} rounded-xl p-6 mb-8 shadow-lg`}>
        <div className="flex items-center gap-3">
          <div className="bg-white bg-opacity-20 rounded-lg p-2">
            <Icon className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-wide m-0">{children}</h1>
        </div>
      </div>
    );
  },
  h2: ({ children }) => {
    const text = extractText(children);
    const style = getSectionStyle(text);
    const Icon = style.icon;
    return (
      <div className={`mt-8 mb-4 border-l-4 ${style.border} ${style.bg} rounded-r-lg p-4 flex items-center gap-3`}>
        <div className={`${style.iconColor} flex-shrink-0`}>
          <Icon className="h-6 w-6" />
        </div>
        <h2 className={`text-xl font-bold ${style.text} m-0`}>{children}</h2>
      </div>
    );
  },
  h3: ({ children }) => {
    const text = extractText(children);
    const style = getSectionStyle(text);
    const domainMatch = text.match(/^(Dom[íi]nio\s+[A-Z0-9]+(?:\s*[-–])?)\s*(.*)/i);
    if (domainMatch) {
      const domainCode = domainMatch[1].replace(/\s*[-–]$/, '');
      const domainName = domainMatch[2];
      return (
        <div className="mt-5 mb-3 flex items-center gap-3">
          <span className={`inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r ${style.gradient} text-white text-sm font-bold tracking-wide shadow-sm whitespace-nowrap`}>
            {domainCode}
          </span>
          <h3 className="text-base font-semibold text-gray-800 m-0">{domainName}</h3>
        </div>
      );
    }
    return (
      <div className={`mt-5 mb-3 ${style.bg} rounded-lg px-4 py-2.5 border ${style.border} border-opacity-30`}>
        <h3 className={`text-base font-bold ${style.text} m-0`}>{children}</h3>
      </div>
    );
  },
  h4: ({ children }) => (
    <h4 className="mt-4 mb-2 text-sm font-semibold text-brand-700 m-0 pl-1">
      {children}
    </h4>
  ),
  h5: ({ children }) => (
    <h5 className="mt-3 mb-1 text-sm font-semibold text-gray-600 uppercase tracking-wider m-0">{children}</h5>
  ),
  p: ({ children }) => (
    <p className="text-gray-700 leading-relaxed mb-3 text-sm">{children}</p>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-gray-200 shadow-sm">
      <table className="markdown-table w-full">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gradient-to-r from-gray-100 to-gray-50">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{children}</td>
  ),
  tr: ({ children, ...props }) => (
    <tr className="hover:bg-gray-50 transition-colors" {...props}>{children}</tr>
  ),
  ul: ({ children }) => (
    <ul className="my-3 space-y-2 pl-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-3 space-y-2 pl-1 list-none counter-reset-item">{children}</ol>
  ),
  li: ({ children }) => {
    const text = extractText(children);
    const isCheck = text.startsWith('✓') || text.startsWith('✔') || text.startsWith('☑');
    return (
      <li className="flex items-start gap-2 text-sm text-gray-700">
        {isCheck ? (
          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-2 flex-shrink-0"></div>
        )}
        <span className="leading-relaxed">{isCheck ? text.slice(1).trim() : children}</span>
      </li>
    );
  },
  blockquote: ({ children }) => (
    <div className="my-4 border-l-4 border-amber-400 bg-amber-50 rounded-r-lg p-4 flex items-start gap-3">
      <Lightbulb className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
      <div className="text-sm text-amber-800">{children}</div>
    </div>
  ),
  strong: ({ children }) => {
    const text = extractText(children);
    const domainMatch = text.match(/^(Dom[íi]nios?\s+[A-Z0-9,\s]+?)(\s*[-–]\s*)(.+)$/i);
    if (domainMatch) {
      const domainCode = domainMatch[1].trim();
      const domainName = domainMatch[3].trim();
      const style = getSectionStyle(domainName);
      return (
        <span className="inline-flex items-center gap-2.5 my-1">
          <span className={`inline-flex items-center px-3 py-1 rounded-md bg-gradient-to-r ${style.gradient} text-white text-xs font-bold tracking-wide shadow-sm`}>
            {domainCode}
          </span>
          <span className="font-bold text-gray-900 text-base">{domainName}</span>
        </span>
      );
    }
    return <strong className="font-semibold text-gray-900">{children}</strong>;
  },
  em: ({ children }) => (
    <em className="text-gray-600 italic">{children}</em>
  ),
  hr: () => (
    <div className="my-6 flex items-center gap-3">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      <Star className="h-3 w-3 text-gray-300" />
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
    </div>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-brand-600 hover:text-brand-800 underline decoration-brand-300 hover:decoration-brand-500 transition-colors" target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  code: ({ children, inline }) => (
    inline
      ? <code className="px-1.5 py-0.5 bg-gray-100 text-brand-700 rounded text-xs font-mono">{children}</code>
      : <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto my-4 text-sm"><code>{children}</code></pre>
  ),
};
